import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable, DataTableSelectionMultipleChangeEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { RadioButton } from 'primereact/radiobutton';
import { Message } from 'primereact/message';
import { lagerFinishedProductsAPI, lagerProductionAPI } from '../services/api';
import { LagerFinishedProduct, LagerProductionRecord, LagerProductionUnit, LagerShift } from '../types';
import { showCustomAlert } from './CustomAlert';
import { useAuth } from '../contexts/AuthContext';

type FilterMode = 'today' | 'all' | 'date';

type ProductionFormState = {
  productId: string;
  quantity: number;
  unit: LagerProductionUnit;
  producedDate: Date;
  shiftSupervisor: string;
  shift: LagerShift;
};

const unitOptions: Array<{ label: string; value: LagerProductionUnit }> = [
  { label: 'kom', value: 'kom' },
  { label: 'paleta', value: 'paleta' }
];

const shiftOptions: Array<{ label: string; value: LagerShift }> = [
  { label: 'I smena', value: 'I' },
  { label: 'II smena', value: 'II' },
  { label: 'III smena', value: 'III' }
];

function toDate(value: string | undefined): Date {
  if (!value) return new Date();
  const d = new Date(value);
  return isNaN(d.getTime()) ? new Date() : d;
}

const LagerProduction: React.FC = () => {
  const { user } = useAuth();

  const [records, setRecords] = useState<LagerProductionRecord[]>([]);
  const [selectedRecords, setSelectedRecords] = useState<LagerProductionRecord[]>([]);
  const [products, setProducts] = useState<LagerFinishedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<LagerProductionRecord | null>(null);
  const [error, setError] = useState('');

  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [filterDate, setFilterDate] = useState<Date>(new Date());

  const [form, setForm] = useState<ProductionFormState>({
    productId: '',
    quantity: 0,
    unit: 'paleta',
    producedDate: new Date(),
    shiftSupervisor: '',
    shift: 'I'
  });

  const selectedProduct = useMemo(() => products.find((p) => p._id === form.productId), [products, form.productId]);

  const calculatedM2 = useMemo(() => {
    const qty = Number(form.quantity || 0);
    if (!selectedProduct || !qty) return 0;
    const m2PerPiece = Number(selectedProduct.m2PerPiece || 0);
    const perPallet = Number(selectedProduct.perPallet || 0);
    if (form.unit === 'kom') return qty * m2PerPiece;
    return qty * perPallet * m2PerPiece;
  }, [form.quantity, form.unit, selectedProduct]);

  const loadProducts = async () => {
    try {
      const data = await lagerFinishedProductsAPI.getAll();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err: any) {
      // Products are required for creating/editing; show a clear message.
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju proizvoda';
      setError(msg);
    }
  };

  const loadRecords = async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {};
      if (filterMode === 'all') params.showAll = true;
      if (filterMode === 'date') params.date = filterDate.toISOString();
      const data = await lagerProductionAPI.getAll(params);
      setRecords(Array.isArray(data) ? data : []);
      setSelectedRecords([]);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju proizvodnje';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMode, filterDate]);

  const canDelete = user?.role === 'admin' || user?.role === 'direktor' || (user?.role === 'lager' && user?.subRole === 'sef_lagera');

  const canEditRecord = (record: LagerProductionRecord) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'direktor') return true;
    if (user.role === 'lager' && user.subRole === 'sef_lagera') return true;
    const createdById = (record.createdBy as any)?._id;
    return (user.role === 'lager' && (user.subRole === 'viljuskarista' || user.subRole === 'hilti') && createdById === user._id);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      productId: '',
      quantity: 0,
      unit: 'paleta',
      producedDate: new Date(),
      shiftSupervisor: '',
      shift: 'I'
    });
    setError('');
    setDialogVisible(true);
  };

  const openEdit = (record: LagerProductionRecord) => {
    setEditing(record);
    setForm({
      productId: record.productId,
      quantity: Number(record.quantity || 0),
      unit: record.unit,
      producedDate: toDate(record.producedDate),
      shiftSupervisor: record.shiftSupervisor || '',
      shift: record.shift
    });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!form.productId) return setError('Proizvod je obavezan');
    if (!form.quantity || form.quantity <= 0) return setError('Količina mora biti veća od 0');
    if (!form.shiftSupervisor.trim()) return setError('Smenovođa je obavezan');
    try {
      setError('');
      const payload = {
        productId: form.productId,
        quantity: form.quantity,
        unit: form.unit,
        producedDate: form.producedDate.toISOString(),
        shiftSupervisor: form.shiftSupervisor.trim(),
        shift: form.shift
      };
      if (editing?._id) {
        await lagerProductionAPI.update(editing._id, payload);
      } else {
        await lagerProductionAPI.create(payload);
      }
      setDialogVisible(false);
      await loadRecords();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju zapisa';
      setError(msg);
      showCustomAlert.error(msg);
    }
  };

  const handleDelete = (record: LagerProductionRecord) => {
    showCustomAlert.confirm(
      'Da li ste sigurni da želite da obrišete zapis proizvodnje?',
      async () => {
        try {
          await lagerProductionAPI.delete(record._id);
          await loadRecords();
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju zapisa';
          showCustomAlert.error(msg);
        }
      },
      'Potvrda brisanja'
    );
  };

  const handleBulkDelete = () => {
    if (!canDelete) return;
    const items = selectedRecords;
    if (!items.length) return;

    showCustomAlert.confirm(
      `Da li ste sigurni da želite da obrišete ${items.length} zapis(a)?`,
      async () => {
        setBulkDeleting(true);
        try {
          const results = await Promise.allSettled(items.map((r) => lagerProductionAPI.delete(r._id)));
          const ok = results.filter((r) => r.status === 'fulfilled').length;
          const failed = results.length - ok;
          if (failed === 0) {
            showCustomAlert.success(`Obrisano: ${ok}`);
          } else {
            showCustomAlert.warning(`Obrisano: ${ok}, neuspešno: ${failed}`);
          }
          await loadRecords();
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju zapisa';
          showCustomAlert.error(msg);
        } finally {
          setSelectedRecords([]);
          setBulkDeleting(false);
        }
      },
      'Potvrda brisanja'
    );
  };

  const handleSelectAll = () => setSelectedRecords(records);
  const handleClearSelection = () => setSelectedRecords([]);

  const dateBody = (row: LagerProductionRecord) => {
    const d = new Date(row.producedDate);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('sr-RS');
  };

  const createdByBody = (row: LagerProductionRecord) => {
    const u = row.createdBy as any;
    if (!u) return '';
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
    return name || u.username || '';
  };

  const actionsBody = (row: LagerProductionRecord) => (
    <div className="flex gap-2 justify-content-end">
      {canEditRecord(row) && (
        <Button icon="pi pi-pencil" className="action-icon-btn" text rounded onClick={() => openEdit(row)} tooltip="Izmeni" />
      )}
      {canDelete && (
        <Button
          icon="pi pi-trash"
          className="action-icon-btn"
          text
          rounded
          severity="danger"
          onClick={() => handleDelete(row)}
          tooltip="Obriši"
        />
      )}
    </div>
  );

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Proizvodnja">
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <div className="flex gap-4 align-items-center flex-wrap">
            <div className="flex align-items-center gap-2">
              <RadioButton inputId="fm-today" name="filterMode" value="today" onChange={() => setFilterMode('today')} checked={filterMode === 'today'} />
              <label htmlFor="fm-today">Danas</label>
            </div>
            <div className="flex align-items-center gap-2">
              <RadioButton inputId="fm-all" name="filterMode" value="all" onChange={() => setFilterMode('all')} checked={filterMode === 'all'} />
              <label htmlFor="fm-all">Svi zapisi</label>
            </div>
            <div className="flex align-items-center gap-2">
              <RadioButton inputId="fm-date" name="filterMode" value="date" onChange={() => setFilterMode('date')} checked={filterMode === 'date'} />
              <label htmlFor="fm-date">Određeni datum</label>
            </div>

            {filterMode === 'date' && (
              <Calendar
                value={filterDate}
                onChange={(e) => setFilterDate((e.value as Date) || new Date())}
                dateFormat="dd.mm.yy"
                appendTo="self"
                showIcon
              />
            )}
          </div>

          <div style={{ marginTop: '0.5rem', color: '#64748b' }}>
            Prikazujem: {filterMode === 'today' ? 'Danas' : filterMode === 'all' ? 'Svi zapisi' : 'Određeni datum'}
          </div>
        </div>

        <div className="flex justify-content-end gap-2" style={{ marginBottom: '1rem' }}>
          {canDelete && (
            <>
              <Button
                icon="pi pi-check-square"
                label="Select all"
                severity="secondary"
                outlined
                onClick={handleSelectAll}
                disabled={!records.length}
              />
              <Button
                icon="pi pi-times"
                label="Poništi"
                severity="secondary"
                outlined
                onClick={handleClearSelection}
                disabled={!selectedRecords.length}
              />
              <Button
                icon="pi pi-trash"
                label={`Obriši izabrane${selectedRecords.length ? ` (${selectedRecords.length})` : ''}`}
                severity="danger"
                outlined
                onClick={handleBulkDelete}
                disabled={!selectedRecords.length || bulkDeleting}
              />
            </>
          )}
          <Button label="Dodaj zapis" icon="pi pi-plus" onClick={openCreate} />
          <Button label="Osveži" icon="pi pi-refresh" severity="secondary" outlined onClick={loadRecords} />
        </div>

        {canDelete ? (
          <DataTable
            value={records}
            loading={loading || bulkDeleting}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            responsiveLayout="scroll"
            dataKey="_id"
            selectionMode="checkbox"
            selection={selectedRecords}
            onSelectionChange={(e: DataTableSelectionMultipleChangeEvent<LagerProductionRecord[]>) => setSelectedRecords(e.value || [])}
          >
            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
            <Column header="Datum" body={dateBody} sortable sortField="producedDate" />
            <Column field="productName" header="Proizvod" sortable />
            <Column field="quantity" header="Količina" />
            <Column field="unit" header="Jedinica" />
            <Column header="m²" body={(r: LagerProductionRecord) => (typeof r.m2Total === 'number' ? r.m2Total.toFixed(2) : '')} />
            <Column field="shiftSupervisor" header="Smenovođa" />
            <Column field="shift" header="Smena" />
            <Column header="Kreirao" body={createdByBody} />
            <Column header="Akcije" body={actionsBody} style={{ width: '160px' }} />
          </DataTable>
        ) : (
          <DataTable
            value={records}
            loading={loading || bulkDeleting}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            responsiveLayout="scroll"
          >
            <Column header="Datum" body={dateBody} sortable sortField="producedDate" />
            <Column field="productName" header="Proizvod" sortable />
            <Column field="quantity" header="Količina" />
            <Column field="unit" header="Jedinica" />
            <Column header="m²" body={(r: LagerProductionRecord) => (typeof r.m2Total === 'number' ? r.m2Total.toFixed(2) : '')} />
            <Column field="shiftSupervisor" header="Smenovođa" />
            <Column field="shift" header="Smena" />
            <Column header="Kreirao" body={createdByBody} />
            <Column header="Akcije" body={actionsBody} style={{ width: '160px' }} />
          </DataTable>
        )}
      </Card>

      <Dialog
        header={editing ? 'Izmeni zapis proizvodnje' : 'Dodaj zapis proizvodnje'}
        visible={dialogVisible}
        style={{ width: '760px', maxWidth: '96vw' }}
        onHide={() => setDialogVisible(false)}
        modal
        closable
      >
        <div className="p-fluid">
          <div className="grid">
            <div className="col-12">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Proizvod</label>
              <Dropdown
                value={form.productId}
                options={products}
                optionLabel="name"
                optionValue="_id"
                placeholder="Izaberite proizvod"
                filter
                showClear
                appendTo="self"
                onChange={(e) => setForm((p) => ({ ...p, productId: e.value || '' }))}
              />
            </div>

            <div className="col-12 md:col-6">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Količina</label>
              <InputNumber value={form.quantity} onValueChange={(e) => setForm((p) => ({ ...p, quantity: Number(e.value || 0) }))} min={0} useGrouping={false} />
            </div>

            <div className="col-12 md:col-6">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Jedinica</label>
              <Dropdown
                value={form.unit}
                options={unitOptions}
                optionLabel="label"
                optionValue="value"
                appendTo="self"
                onChange={(e) => setForm((p) => ({ ...p, unit: e.value }))}
              />
            </div>

            <div className="col-12 md:col-6">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Smenovođa</label>
              <InputText value={form.shiftSupervisor} onChange={(e) => setForm((p) => ({ ...p, shiftSupervisor: e.target.value }))} placeholder="Unesite ime smenovođe" />
            </div>

            <div className="col-12 md:col-6">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Smena</label>
              <Dropdown
                value={form.shift}
                options={shiftOptions}
                optionLabel="label"
                optionValue="value"
                appendTo="self"
                onChange={(e) => setForm((p) => ({ ...p, shift: e.value }))}
              />
            </div>

            <div className="col-12">
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Datum proizvodnje</label>
              <Calendar
                value={form.producedDate}
                onChange={(e) => setForm((p) => ({ ...p, producedDate: (e.value as Date) || new Date() }))}
                dateFormat="dd.mm.yy"
                appendTo="self"
                showIcon
                style={{ width: '100%' }}
              />
            </div>

            <div className="col-12">
              <div className="p-message p-message-info" style={{ marginTop: '0.25rem', padding: '0.5rem 0.75rem' }}>
                <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }} />
                Kalkulisano: {calculatedM2.toFixed(2)} m²
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-content-end" style={{ marginTop: '1.25rem' }}>
            <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} />
            <Button label="Otkaži" icon="pi pi-times" severity="secondary" outlined onClick={() => setDialogVisible(false)} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LagerProduction;


